import { useSelector } from 'react-redux';
import { hasPermissionSelector, permissionsSetSelector } from './redux.selectors';

/**
 * Hook to check if user has a specific permission
 * @param {string} permissionName - The name of the permission to check
 * @returns {boolean} - True if user has the permission
 * 
 * @example
 * const canCreateAttendance = usePermission('attendance.create attendance');
 */
export const usePermission = (permissionName) => {
    return useSelector((state) => hasPermissionSelector(state, permissionName));
};

/**
 * Hook to check multiple permissions at once
 * @param {string[]} permissionNames - Array of permission names to check
 * @returns {boolean} - True if user has ALL permissions
 * 
 * @example
 * const hasAllPermissions = usePermissions(['attendance.create attendance', 'attendance.delete an attendance']);
 */
export const usePermissions = (permissionNames) => {
    const permissionsSet = useSelector(permissionsSetSelector);
    
    return permissionNames.every((permission) => permissionsSet.has(permission));
};

/**
 * Hook to check if user has at least one of the specified permissions
 * @param {string[]} permissionNames - Array of permission names to check
 * @returns {boolean} - True if user has at least one permission
 * 
 * @example
 * const hasAnyPermission = useAnyPermission(['attendance.create attendance', 'attendance.update attendance']);
 */
export const useAnyPermission = (permissionNames) => {
    const permissionsSet = useSelector(permissionsSetSelector);
    
    return permissionNames.some((permission) => permissionsSet.has(permission));
};
